import { Controller } from "@hotwired/stimulus";
import * as bootstrap from "bootstrap";

// Connects to data-controller="products"
export default class extends Controller {
  static targets = ["order", "firstRow", "selectableProduct", "emptyState", "subTotal", "total"];
  static values = { currency: "String", shippingCost: Number, freeShippingThreshold: Number, maxDiscount: Number }

  connect() {
    this.recalculateTotals()
  }

  selectableProductTargetConnected(target) {
    if (
      document.querySelector(
        `input[value="${target.dataset.productsIdParam}"][name="order[order_products][][product_id]"]`
      )
    )
      target.setAttribute("disabled", "true");
  }

  recalculateTotals(){
    const newTotalPrice = Array.from(document.querySelectorAll('td[name="product-total"]')).map((element) => {
      const raw = element.dataset.rawTotal
      const currentPrice = raw !== undefined ? parseFloat(raw) : parseFloat(element.innerText.replace('.', '').replace(',', '.').replaceAll(/\W\s+/g, ''))
      return isNaN(currentPrice) ? 0 : currentPrice
    }).reduce((accumulator, currentValue) => accumulator + currentValue, 0)

    const currency = this.currencyValue.toLowerCase() === 'usd' ? '$' : '€'
    const shipping = newTotalPrice >= this.freeShippingThresholdValue ? 0 : this.shippingCostValue
    document.getElementById('subtotal-price').innerText = `${currency} ${newTotalPrice.toLocaleString('nl', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
    document.getElementById('shipping-cost').innerText = `${currency} ${shipping.toLocaleString('nl', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
    document.getElementById('total-price').innerText = `${currency} ${(newTotalPrice + shipping).toLocaleString('nl', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`
  }

  async deleteProduct(event) {
    const row = event.target.closest('tr');
    const url = event.params.url;

    const response = await fetch(url, { method: 'DELETE', headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content } });
    const json = await response.json();
    if (json.success === true) {
      if (row.parentElement.querySelectorAll("tr:not(.d-none)").length - 1 === 0)
        this.emptyStateTarget.classList.remove("d-none");
      row.remove();
    }

    this.recalculateTotals()
  }

  updateTotals(event) {
    const row = event.target.closest('tr');
    const totalProductPriceField = row.querySelector('td[name="product-total"]')
    const amountField = row.querySelector('input[id*="__amount"]')
    const discountField = row.querySelector('input[id*="__discount"]')

    if (amountField.value !== '') {
      amountField.value = Math.max(0, parseFloat(amountField.value) || 0)
    }

    if (discountField.value !== '') {
      const min = parseFloat(discountField.min ?? 0)
      const max = parseFloat(discountField.max ?? Infinity)
      discountField.value = Math.min(max, Math.max(min, parseFloat(discountField.value) || 0))
    }

    const price = parseFloat(row.querySelector('td[name="product-price"]').innerText.replace(',', '.').replaceAll(/\W\s+/g, ''))
    const discountValue = discountField.value !== '' ? parseFloat(discountField.value) : parseFloat(discountField.dataset.discount || 0)
    const discountedUnitPrice = parseFloat((price - (price * discountValue / 100)).toFixed(2))
    const totalProductPrice = amountField.value * discountedUnitPrice
    const currency = this.currencyValue.toLowerCase() === 'usd' ? '$' : '€'
    totalProductPriceField.dataset.rawTotal = totalProductPrice
    totalProductPriceField.innerText = `${currency} ${totalProductPrice.toLocaleString('nl', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`

    this.updateDiscountBadge(discountField)
    this.recalculateTotals()
  }

  removeProduct(event) {
    const row = event.target.closest('tr');
    if (row.parentElement.querySelectorAll("tr:not(.d-none)").length - 1 === 0)
      this.emptyStateTarget.classList.remove("d-none");
    row.remove();
    this.recalculateTotals()
  }

  addProduct(event) {
    const newRow = document.createElement("tr");
    newRow.innerHTML = this.firstRowTarget.innerHTML;
    newRow.removeAttribute("data-products-target");
    newRow.removeAttribute("class");
    newRow.querySelector('input[id*="product_id"]').value = event.params.id;
    newRow.querySelector('input[id*="price_id"]').value = event.params.priceId;
    newRow.querySelector('td[name="product-name"]').innerText = event.params.name;
    newRow.querySelector('td[name="product-price"]').innerText = event.params.price;

    const discountField = newRow.querySelector('input[id*="__discount"]')
    discountField.dataset.discount = event.params.discount
    discountField.dataset.discountTitle = event.params.discountSource
    discountField.min = event.params.discount
    if (event.params.discount >= this.maxDiscountValue) {
      discountField.disabled = true
    }

    this.updateDiscountBadge(discountField)

    event.target.setAttribute("disabled", true);
    this.emptyStateTarget.classList.add("d-none");

    if(this.orderTarget.querySelector('.total-row'))
      this.orderTarget.querySelector('.total-row').insertAdjacentElement("beforeBegin", newRow);
    else
      this.orderTarget.insertAdjacentElement("beforeend", newRow);
  }

  updateDiscountBadge(discountField) {
    const badge = discountField.closest('tr').querySelector('[data-discount-badge]')
    if (!badge) return

    const manualValue = discountField.value !== '' ? parseFloat(discountField.value) : null
    const computedValue = parseFloat(discountField.dataset.discount || 0)
    const value = manualValue ?? computedValue
    const title = manualValue !== null ? 'Custom discount' : (discountField.dataset.discountTitle || badge.dataset.discountTitle || '')

    if (value > 0 || manualValue !== null) {
      badge.innerText = `- ${value}%`
      badge.dataset.bsTitle = title
      badge.classList.remove('invisible')
    } else {
      badge.classList.add('invisible')
    }

    bootstrap.Tooltip.getInstance(badge)?.dispose()
    new bootstrap.Tooltip(badge)
  }
}
